
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MasterNavigation from '../../components/feature/MasterNavigation';
import TurnstileWidget from '../../components/feature/TurnstileWidget';
import { isTurnstileEnabled, submitProtectedForm } from '../../utils/form';

const ClientRegistrationPage: React.FC = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    industryType: '',
    companySize: '',
    servicesNeeded: [] as string[],
    currentChallenges: '',
    complianceRequirements: '',
    preferredStartDate: '',
    budget: '',
    additionalInfo: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [verificationError, setVerificationError] = useState('');

  const serviceOptions = [
    'Security Engineering & Hardening',
    'Cloud Platform & DevSecOps',
    'AI Security & Guardrails',
    'External Penetration Testing',
    'Web Application Security',
    'Internal Network Assessment',
    'Phishing Simulations',
    'Risk & Exposure Analysis',
    'Blockchain & Web3 Security',
    'Mobile Security Testing',
    'IoT Security Testing',
    'OT Security Reviews',
    'Digital Forensics & Incident Response'
  ];

  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      servicesNeeded: prev.servicesNeeded.includes(service)
        ? prev.servicesNeeded.filter(s => s !== service)
        : [...prev.servicesNeeded, service]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isTurnstileEnabled && !turnstileToken) {
      setSubmitStatus('error');
      setVerificationError('Please complete the verification challenge before submitting your registration.');
      return;
    }

    setSubmitStatus('submitting');
    setVerificationError('');

    const submitData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        submitData.append(key, value.join(', '));
      } else {
        submitData.append(key, value);
      }
    });
    if (turnstileToken) {
      submitData.append('cf-turnstile-response', turnstileToken);
    }

    try {
      const response = await submitProtectedForm('client-registration', submitData);

      if (response.ok) {
        setSubmitStatus('success');
        setTurnstileToken('');
        setTurnstileResetKey((value) => value + 1);
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          industryType: '',
          companySize: '',
          servicesNeeded: [],
          currentChallenges: '',
          complianceRequirements: '',
          preferredStartDate: '',
          budget: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
        setTurnstileToken('');
        setTurnstileResetKey((value) => value + 1);
      }
    } catch {
      setSubmitStatus('error');
      setTurnstileToken('');
      setTurnstileResetKey((value) => value + 1);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/fc9008f476a46e5d11a11722891fc192/828492aec85b774ecdf3a4106883a9bc.png" alt="TIDHQ.NETWORK" className="h-10 w-10 object-contain" />
            <span className="text-white font-light text-lg tracking-wide">TIDHQ.NETWORK</span>
          </Link>
          <div className="flex items-center gap-6">
            <MasterNavigation currentPath="/client-registration" />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 bg-gradient-to-b from-[#0a1a14] to-black">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-[#6B2737] text-xs tracking-[0.2em] uppercase mb-4 font-medium">Client Registration</div>
          <h1 className="text-5xl font-light text-white mb-6">Partner with TIDHQ.NETWORK</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Register your organization for authorized security testing, architecture review, or engineering support across security, cloud, AI, and related systems.
          </p>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} data-readdy-form className="space-y-8">
            
            {/* Company Information */}
            <div className="bg-[#1C1C1C] rounded-2xl p-8 border-l-4 border-[#6B2737]">
              <h2 className="text-2xl font-light text-white mb-6">Company Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label htmlFor="industryType" className="block text-sm font-medium text-gray-300 mb-2">
                    Industry Type *
                  </label>
                  <select
                    id="industryType"
                    name="industryType"
                    required
                    value={formData.industryType}
                    onChange={(e) => setFormData(prev => ({ ...prev, industryType: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                  >
                    <option value="">Select Industry</option>
                    <option value="Technology">Technology</option>
                    <option value="Financial Services">Financial Services</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Government">Government</option>
                    <option value="Education">Education</option>
                    <option value="Retail">Retail</option>
                    <option value="Energy">Energy</option>
                    <option value="Aerospace">Aerospace</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="companySize" className="block text-sm font-medium text-gray-300 mb-2">
                    Company Size *
                  </label>
                  <select
                    id="companySize"
                    name="companySize"
                    required
                    value={formData.companySize}
                    onChange={(e) => setFormData(prev => ({ ...prev, companySize: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                  >
                    <option value="">Select Size</option>
                    <option value="1-50 employees">1-50 employees</option>
                    <option value="51-200 employees">51-200 employees</option>
                    <option value="201-1000 employees">201-1000 employees</option>
                    <option value="1000+ employees">1000+ employees</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-300 mb-2">
                    Budget Range
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                  >
                    <option value="">Select Budget Range</option>
                    <option value="$10K - $25K">$10K - $25K</option>
                    <option value="$25K - $50K">$25K - $50K</option>
                    <option value="$50K - $100K">$50K - $100K</option>
                    <option value="$100K+">$100K+</option>
                    <option value="Discuss during consultation">Discuss during consultation</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-[#1C1C1C] rounded-2xl p-8 border-l-4 border-[#6B2737]">
              <h2 className="text-2xl font-light text-white mb-6">Contact Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-300 mb-2">
                    Primary Contact Name *
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    required
                    value={formData.contactName}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                    placeholder="contact@yourcompany.com"
                  />
                </div>
              </div>
            </div>

            {/* Services Needed */}
            <div className="bg-[#1C1C1C] rounded-2xl p-8 border-l-4 border-[#6B2737]">
              <h2 className="text-2xl font-light text-white mb-6">Services Needed</h2>
              <p className="text-gray-400 text-sm mb-6">Select all services your organization is exploring:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {serviceOptions.map((service) => (
                  <label key={service} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.servicesNeeded.includes(service)}
                      onChange={() => handleServiceChange(service)}
                      className="mt-1 h-4 w-4 text-[#6B2737] focus:ring-[#6B2737] border-gray-600 rounded"
                    />
                    <span className="text-gray-300 text-sm group-hover:text-white transition-colors">
                      {service}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-[#1C1C1C] rounded-2xl p-8 border-l-4 border-[#6B2737]">
              <h2 className="text-2xl font-light text-white mb-6">Additional Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="currentChallenges" className="block text-sm font-medium text-gray-300 mb-2">
                    Current Security or Engineering Challenges
                  </label>
                  <textarea
                    id="currentChallenges"
                    name="currentChallenges"
                    rows={4}
                    maxLength={500}
                    value={formData.currentChallenges}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentChallenges: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white resize-none"
                    placeholder="Describe your current security, cloud, AI, or engineering challenges..."
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.currentChallenges.length}/500 characters</p>
                </div>

                <div>
                  <label htmlFor="complianceRequirements" className="block text-sm font-medium text-gray-300 mb-2">
                    Compliance Requirements
                  </label>
                  <input
                    type="text"
                    id="complianceRequirements"
                    name="complianceRequirements"
                    value={formData.complianceRequirements}
                    onChange={(e) => setFormData(prev => ({ ...prev, complianceRequirements: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                    placeholder="e.g., NIST, ISO 27001, HIPAA, PCI DSS, SOC 2"
                  />
                </div>

                <div>
                  <label htmlFor="preferredStartDate" className="block text-sm font-medium text-gray-300 mb-2">
                    Preferred Start Date
                  </label>
                  <input
                    type="date"
                    id="preferredStartDate"
                    name="preferredStartDate"
                    value={formData.preferredStartDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferredStartDate: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white"
                  />
                </div>

                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-300 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    rows={4}
                    maxLength={500}
                    value={formData.additionalInfo}
                    onChange={(e) => setFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#6B2737] focus:border-transparent text-white resize-none"
                    placeholder="Any additional information about your security, cloud, AI, or engineering needs..."
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <TurnstileWidget
                key={turnstileResetKey}
                onTokenChange={(token) => {
                  setTurnstileToken(token);
                  if (token) {
                    setVerificationError('');
                  }
                }}
                theme="dark"
                className="mb-6"
              />
              {verificationError && (
                <div className="bg-red-900/50 border border-red-600 rounded-lg p-4 text-center text-red-300 text-sm mb-6">
                  {verificationError}
                </div>
              )}
              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="bg-[#6B2737] hover:bg-[#8B3747] text-white px-12 py-4 rounded-lg text-lg font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitStatus === 'submitting' ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting Registration...
                  </span>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Submit Registration
                  </>
                )}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-900/50 border border-green-600 rounded-lg p-6 text-center">
                <i className="ri-check-circle-line text-green-400 text-3xl mb-3"></i>
                <h3 className="text-green-400 font-medium text-lg mb-2">Registration Successful!</h3>
                <p className="text-green-300 text-sm">
                  Thank you for registering with TIDHQ.NETWORK. Our team will review your submission and contact you within 24 hours to discuss your security needs and next steps.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-900/50 border border-red-600 rounded-lg p-6 text-center">
                <i className="ri-error-warning-line text-red-400 text-3xl mb-3"></i>
                <h3 className="text-red-400 font-medium text-lg mb-2">Registration Failed</h3>
                <p className="text-red-300 text-sm">
                  There was an error submitting your registration. Please check your information and try again, or contact us directly.
                </p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Security Notice */}
      <section className="py-16 px-6 bg-[#6B2737]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-light text-white mb-4">Your Information is Secure</h2>
          <p className="text-gray-200 leading-relaxed">
            All registration information is transmitted securely and stored with enterprise-grade encryption. We strictly adhere to our privacy policy and ethical guidelines. Your data will only be used for engagement planning and will never be shared with third parties.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/fc9008f476a46e5d11a11722891fc192/828492aec85b774ecdf3a4106883a9bc.png" alt="TIDHQ.NETWORK" className="h-8 w-8 object-contain" />
                <span className="text-white text-xl font-light">TIDHQ.NETWORK</span>
              </div>
              <div className="text-gray-500 text-sm mb-4">Ethical Security Validation</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Authorized cybersecurity testing for organizations requiring trusted, independent security assessments.
              </p>
            </div>
            <div>
              <h4 className="text-white text-sm uppercase tracking-wider mb-6">Quick Links</h4>
              <div className="space-y-3">
                <Link to="/services" className="block text-gray-500 hover:text-white text-sm transition-colors hover:underline">Services</Link>
                <Link to="/how-it-works" className="block text-gray-500 hover:text-white text-sm transition-colors hover:underline">Process</Link>
                <Link to="/who-this-is-for" className="block text-gray-500 hover:text-white text-sm transition-colors hover:underline">Who This Is For</Link>
                <Link to="/security-ethics" className="block text-gray-500 hover:text-white text-sm transition-colors hover:underline">Ethics Statement</Link>
                <Link to="/blog" className="block text-gray-500 hover:text-white text-sm transition-colors hover:underline">Blog</Link>
                <Link to="/contact" className="block text-gray-500 hover:text-white text-sm transition-colors hover:underline">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white text-sm uppercase tracking-wider mb-6">Contact</h4>
              <div className="space-y-3 text-gray-500 text-sm">
                <div>contact@tidhq.net</div>
                <div className="mt-4">
                  <a href="https://tidhq.net" target="_blank" rel="noopener noreferrer" className="text-[#6B2737] hover:text-[#8B3747] transition-colors">
                    TIDHQ.NET
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-[#1C1C1C] px-6 py-6">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#6B2737] transition-colors">
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#6B2737] transition-colors">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
            </div>
            <div className="text-gray-600 text-sm text-center">
              © 2026 TIDHQ.NETWORK. All testing performed with authorization.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientRegistrationPage;
